import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Plus, AlertTriangle, CheckCircle2, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { superadminApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { REGION_LABELS } from '../lib/regions';
import { toast } from 'sonner';

/**
 * Superadmin → Public Holidays.
 *
 * Bank holidays are held ONCE for the whole platform (`public_holidays.tenant_id IS
 * NULL`) rather than copied into every customer. This screen is the other half of that
 * decision: somewhere to see what's loaded, how far ahead it runs, and to add a one-off
 * national day that reaches every customer at once.
 *
 * The runway column is the part with daily value. Before it, the only way to discover
 * that a region was running dry was to notice allowances going wrong.
 */
// ⚠️ ONE REGION VOCABULARY — `lib/regions.js`. This file said "England and Wales"
// while the screens where an admin CHOOSES a region said "England & Wales".

const PublicHolidaysManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', regions: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await superadminApi.getPublicHolidayStatus();
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load holiday status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const topUpNow = async () => {
    setRefreshing(true);
    try {
      const { data: res } = await superadminApi.refreshPublicHolidays();
      toast.success(res.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not top up');
    } finally {
      setRefreshing(false);
    }
  };

  const removeManual = async (h) => {
    const where = REGION_LABELS[h.region] || h.region || 'all regions';
    if (!window.confirm(
      `Remove "${h.name}" on ${formatDateGB(h.date)} from ${where}?\n\n`
      + `Every customer in that region loses this day.`)) return;
    try {
      const { data: res } = await superadminApi.removePublicHoliday(h.id);
      toast.success(res.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove that holiday');
    }
  };

  const toggleRegion = (r) => setForm((f) => ({
    ...f,
    regions: f.regions.includes(r) ? f.regions.filter((x) => x !== r) : [...f.regions, r],
  }));

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!form.name || !form.date || form.regions.length === 0) {
      toast.error('Name, date and at least one region');
      return;
    }
    const where = form.regions.map((r) => REGION_LABELS[r] || r).join(', ');
    if (!window.confirm(
      `Add "${form.name}" on ${formatDateGB(form.date)} for ${where}?\n\n`
      + `Every customer in those regions gets this day.`)) return;

    setAdding(true);
    try {
      const { data: res } = await superadminApi.addPublicHoliday(form);
      toast.success(res.message);
      setForm({ name: '', date: '', regions: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add that holiday');
    } finally {
      setAdding(false);
    }
  };

  const regions = data?.regions || [];
  const lowRegions = regions.filter((r) => r.needs_topping_up);

  return (
    <div className="space-y-4">
      <div className="card-hive p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-3xl">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Globe className="w-4 h-4" /> Public holidays, held once for everyone
            </h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Every customer shares one set of bank holidays per region, so a correction
              is made once rather than per company. They top up automatically each night
              while there's less than {data?.runway_days ?? 365} days ahead loaded —
              customers never import anything.
            </p>
          </div>
          <Button variant="outline" onClick={topUpNow} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Top up now
          </Button>
        </div>

        {!loading && (
          <div className={`mt-5 rounded-lg px-4 py-3 text-sm ${
            lowRegions.length
              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
              : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'}`}>
            {lowRegions.length
              ? <><strong>{lowRegions.length}</strong> region{lowRegions.length === 1 ? ' is' : 's are'} running low and will top up on the next nightly run.</>
              : <>Every region in use is loaded more than a year ahead.</>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="card-hive p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="card-hive overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left p-4 font-medium">Region</th>
                <th className="text-left p-4 font-medium">This year</th>
                <th className="text-left p-4 font-medium">Still to come</th>
                <th className="text-left p-4 font-medium">Covered to</th>
                <th className="text-left p-4 font-medium">Where from</th>
              </tr>
            </thead>
            <tbody>
              {regions.map((r) => (
                <tr key={r.region} className="border-b border-border/50" data-testid={`region-${r.region}`}>
                  <td className="p-4 font-medium">{REGION_LABELS[r.region] || r.region}</td>
                  {/* "This year" is the number that means something. The raw total
                      includes roughly a decade of history from the gov.uk feed, which
                      read as alarmingly large on its own — it's kept as a hint. */}
                  <td className="p-4">
                    {r.this_year} day{r.this_year === 1 ? '' : 's'}
                    <span className="block text-xs text-muted-foreground">
                      {r.loaded} held since {r.earliest_date ? new Date(r.earliest_date).getFullYear() : '—'}
                    </span>
                  </td>
                  <td className="p-4 text-muted-foreground">{r.upcoming}</td>
                  <td className="p-4">
                    {r.furthest_date ? (
                      <span className={r.needs_topping_up ? 'text-amber-600 dark:text-amber-500' : ''}>
                        {formatDateGB(r.furthest_date)}
                        {r.needs_topping_up && (
                          <AlertTriangle className="w-3.5 h-3.5 inline ml-1.5 -mt-0.5" />
                        )}
                      </span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-500">nothing loaded</span>
                    )}
                  </td>
                  <td className="p-4 text-muted-foreground text-xs">
                    {Object.entries(r.sources || {})
                      .map(([src, n]) => `${n} ${src}`).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* One-off days already added — the half I forgot first time round. The delete
          endpoint existed from the start, but with nowhere to see what had been added
          it was unreachable, which is the same mistake as building the opt-out restore
          endpoint without a button. */}
      {(data?.manual || []).length > 0 && (
        <div className="card-hive overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-heading font-semibold">One-off days you've added</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Added by hand rather than coming from an official feed. Removing one takes
              it off every customer in that region.
            </p>
          </div>
          <div className="divide-y divide-border">
            {data.manual.map((h) => (
              <div key={h.id} className={`p-4 flex items-center justify-between gap-4 ${h.past ? 'opacity-60' : ''}`}>
                <div>
                  <p className="font-medium">
                    {h.name}
                    {h.past && <span className="ml-2 text-xs text-muted-foreground">(past)</span>}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDateGB(h.date)} · {REGION_LABELS[h.region] || h.region || 'All regions'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => removeManual(h)}
                  data-testid={`remove-manual-${h.id}`}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A one-off national day. This is the reason the shared model was worth
          building: a coronation is announced, you add one row per region, and every
          customer has it tonight. */}
      <div className="card-hive p-6">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add a one-off national holiday
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
          For a coronation, a state funeral, or anything a government declares between
          the official feeds being published. It appears for every customer in the
          regions you choose, immediately — nobody has to import anything.
        </p>

        <form onSubmit={addHoliday} className="mt-5 space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. State funeral"
                className="mt-1"
                data-testid="adhoc-name"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1"
                data-testid="adhoc-date"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Regions</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {regions.map((r) => (
                <button
                  key={r.region}
                  type="button"
                  onClick={() => toggleRegion(r.region)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    form.regions.includes(r.region)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'}`}
                  data-testid={`adhoc-region-${r.region}`}
                >
                  {REGION_LABELS[r.region] || r.region}
                </button>
              ))}
              {regions.length > 1 && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    regions: f.regions.length === regions.length ? [] : regions.map((r) => r.region),
                  }))}
                  className="px-3 py-1.5 rounded-lg text-sm text-primary hover:underline"
                >
                  {form.regions.length === regions.length ? 'Clear' : 'Whole country'}
                </button>
              )}
            </div>
          </div>

          <Button type="submit" disabled={adding} data-testid="adhoc-submit">
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Add for {form.regions.length || 'no'} region{form.regions.length === 1 ? '' : 's'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PublicHolidaysManager;
